import type { PlatformMessagingNotice, PlatformMessagingNoticeReceipt } from '@exylon/generated-contract-kernel/types';
import {
  NOTICE_INBOX_OPERATION_NAMES,
  type NoticeInboxRefusal,
  type NoticeInboxPage,
  type NoticeReceiptAction,
  type NoticeInboxReadResult,
  type NoticeInboxPort,
  type NoticeInboxScope,
} from './contract-port';
import { isCanonicalUuid } from '../../runtime/format';

/** The open inbox shows the latest page and loads older pages of the same size on demand. */
export const NOTICE_INBOX_PAGE_SIZE = 50;
/** The most notices one request reads (backend/src/platform/messaging/notice-http.ts). */
const NOTICE_INBOX_MAX_REQUEST = 200;
/** A closed inbox polls only the exact unread total; the one notice it carries is not shown. */
export const NOTICE_INBOX_SUMMARY_LIMIT = 1;
/** The most pages an open inbox keeps loaded on demand, not a statement about the recipient's history. */
export const NOTICE_INBOX_MAX_PAGES = 10;

const DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/i;
const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
const isTimestamp = (value: unknown): value is string => typeof value === 'string' && DATE_TIME.test(value) && Number.isFinite(Date.parse(value));
const isOptionalTimestamp = (value: unknown): value is string | null => value === null || isTimestamp(value);
const isPositiveInteger = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
const isScope = (scope: NoticeInboxScope): boolean => isRecord(scope) && isCanonicalUuid(scope.tenantId) && isCanonicalUuid(scope.restaurantId) && isCanonicalUuid(scope.subjectUserId);
const unavailable = (reason: 'transport' | 'authorization_denied' | 'invalid_response', code: string): NoticeInboxReadResult<never> => ({ kind: 'unavailable', reason, code });
const refused = (refusal: NoticeInboxRefusal): NoticeInboxReadResult<never> => unavailable(
  refusal.reason === 'authorization_denied' ? 'authorization_denied' : refusal.reason === 'unavailable' ? 'transport' : 'invalid_response',
  `notice_inbox_${refusal.reason}`,
);

/**
 * IdentityHttpClient validated this page against the generated
 * `PlatformMessagingNoticeList` schema (ids, lengths, enums, the priority
 * range, integers, timestamps, the page size). What remains is what the schema
 * cannot say: the recipient's scope, blank copy, the category grammar and the
 * cross-field receipt and dismissal rules.
 */
function isNotice(notice: PlatformMessagingNotice, scope: NoticeInboxScope): boolean {
  return notice.restaurantId === scope.restaurantId && notice.tenantId === scope.tenantId
    && notice.title.trim() !== '' && notice.body.trim() !== ''
    && /^[a-z][a-z0-9_.:-]{1,159}$/.test(notice.category)
    && ((notice.readAt === null && notice.dismissedAt === null) || notice.seenAt !== null)
    && (notice.dismissPolicy === 'auto') === (notice.autoDismissSeconds !== null);
}

type NoticeKey = Readonly<{ createdAt: string; id: string }>;
const isBefore = (candidate: NoticeKey, previous: NoticeKey): boolean => {
  const difference = Date.parse(candidate.createdAt) - Date.parse(previous.createdAt);
  return difference < 0 || (difference === 0 && candidate.id.toLowerCase() < previous.id.toLowerCase());
};

/** Whether a read of the newest pages reached the notice's place in the inbox order:
 * a notice it covers and does not return is absent, one beyond it is only unread by this read. */
export function noticeInboxCovers(inbox: Pick<NoticeInboxPage, 'items' | 'complete'>, notice: NoticeKey): boolean {
  const last = inbox.items.at(-1);
  return inbox.complete || (last !== undefined && !isBefore(notice, last));
}

const unread = (notice: PlatformMessagingNotice): boolean => notice.readAt === null && notice.dismissedAt === null;

/**
 * Reads the newest `notices` notices of the recipient's inbox, in as few requests as the route's 200 per request
 * allows, and the exact unread total of the whole inbox the Backend counts with the first request. A closed inbox asks
 * for `NOTICE_INBOX_SUMMARY_LIMIT`: one bounded read, whatever the history.
 */
export async function readNoticeInbox(
  port: Pick<NoticeInboxPort, 'readInbox'>,
  scope: NoticeInboxScope,
  { notices = NOTICE_INBOX_PAGE_SIZE, staffNoticesOnly = false, withStaffNotices = false, entityTag = null }: Readonly<{
    notices?: number;
    /** Only the team notices still shown (`only=staff_notices`); the unread total still covers the whole inbox. */
    staffNoticesOnly?: boolean;
    /** The team notices too, in the same request (`also=staff_notices`): a staff screen with the inbox open. */
    withStaffNotices?: boolean;
    /** The revision on screen: its first page's read is conditional. */
    entityTag?: string | null;
  }> = {},
): Promise<NoticeInboxReadResult<NoticeInboxPage> | Readonly<{ kind: 'not_modified' }>> {
  if (!isScope(scope)) return unavailable('authorization_denied', 'notice_inbox_scope_invalid');
  if (!Number.isSafeInteger(notices) || notices < 1 || notices > NOTICE_INBOX_PAGE_SIZE * NOTICE_INBOX_MAX_PAGES) {
    return unavailable('invalid_response', 'notice_inbox_window_invalid');
  }
  const items: PlatformMessagingNotice[] = [];
  const ids = new Set<string>();
  const cursors = new Set<string>();
  let cursor: NoticeKey | null = null;
  let unreadCount: number | null = null;
  let revision: string | null = null;
  let staffNotices: PlatformMessagingNotice[] | null = null;
  const extras = () => ({ ...(revision === null ? {} : { revision }), ...(staffNotices === null ? {} : { staffNotices }) });
  try {
    for (let pageNumber = 0; items.length < notices; pageNumber += 1) {
      const pageSize = Math.min(notices - items.length, NOTICE_INBOX_MAX_REQUEST);
      // backend/src/platform/messaging/notice-http.ts — selectedRestaurantId and noticeCursor use these exact names.
      const command: Parameters<NoticeInboxPort['readInbox']>[0] = {
        operation: NOTICE_INBOX_OPERATION_NAMES.readInbox,
        params: {},
        query: {
          restaurantId: scope.restaurantId,
          limit: String(pageSize),
          ...(cursor === null ? {} : { beforeCreatedAt: cursor.createdAt, beforeId: cursor.id }),
          ...(staffNoticesOnly ? { only: 'staff_notices' } : {}),
          ...(withStaffNotices ? { also: 'staff_notices' } : {}),
        },
      };
      const response = pageNumber === 0 && entityTag !== null ? await port.readInbox(command, entityTag) : await port.readInbox(command);
      if (response === null || typeof response !== 'object' || Array.isArray(response)) return unavailable('invalid_response', 'notice_inbox_page_invalid');
      if (response.kind === 'canonical_refusal') return refused(response);
      if (response.kind === 'not_modified') return response;
      if (response.kind !== 'success') return unavailable('invalid_response', 'notice_inbox_page_invalid');
      const page = response.canonical;
      if (pageNumber === 0) {
        revision = response.entityTag ?? null;
        if (withStaffNotices) {
          if (!Array.isArray(page.staffNotices) || !page.staffNotices.every((notice) => isNotice(notice, scope))) {
            return unavailable('invalid_response', 'notice_inbox_staff_notices_invalid');
          }
          staffNotices = [...page.staffNotices];
        }
      }
      // The total and the page come from one snapshot: the page cannot hold more
      // unread notices than the whole inbox, and a final first page holds them all.
      const pageUnread = page.items.filter(unread).length;
      if (!Number.isSafeInteger(page.unreadCount) || page.unreadCount < pageUnread
        || (!staffNoticesOnly && pageNumber === 0 && page.nextCursor === null && page.unreadCount !== pageUnread)) {
        return unavailable('invalid_response', 'notice_inbox_unread_total_invalid');
      }
      unreadCount ??= page.unreadCount;
      let previous: NoticeKey | null = cursor;
      for (const notice of page.items) {
        if (!isNotice(notice, scope) || ids.has(notice.id.toLowerCase()) || (previous !== null && !isBefore(notice, previous))) {
          return unavailable('invalid_response', 'notice_inbox_notice_or_scope_invalid');
        }
        ids.add(notice.id.toLowerCase());
        items.push(notice);
        previous = notice;
      }
      // backend/src/platform/messaging/notice-http.ts — queueCount is this page's items.length, never an unread total.
      if (page.nextCursor === null) return { kind: 'ready', value: { items, complete: true, unreadCount, ...extras() } };
      const next = page.nextCursor;
      const last = page.items.at(-1);
      if (!last || page.items.length !== pageSize
        || next.createdAt !== last.createdAt || next.id !== last.id
        || (cursor !== null && !isBefore(next, cursor))) return unavailable('invalid_response', 'notice_inbox_cursor_invalid');
      const cursorKey = `${next.createdAt}:${next.id.toLowerCase()}`;
      if (cursors.has(cursorKey)) return unavailable('invalid_response', 'notice_inbox_cursor_repeated');
      cursors.add(cursorKey);
      cursor = next;
    }
    if (unreadCount === null) return unavailable('invalid_response', 'notice_inbox_unread_total_invalid');
    return { kind: 'ready', value: { items, complete: false, unreadCount, ...extras() } };
  } catch {
    // A later page failing does not turn the preceding partial pages into a complete inbox.
    return unavailable('transport', 'notice_inbox_read_failed');
  }
}

/** backend/src/platform/messaging/schema.ts — record_notice_receipt accumulates timestamps with COALESCE. */
export function applyNoticeReceipt(
  notice: PlatformMessagingNotice,
  receipt: PlatformMessagingNoticeReceipt,
  action: NoticeReceiptAction,
): PlatformMessagingNotice | null {
  if (!isRecord(receipt) || receipt.noticeId !== notice.id || typeof receipt.changed !== 'boolean'
    || !isPositiveInteger(receipt.version) || receipt.version < notice.version
    || !isOptionalTimestamp(receipt.seenAt) || !isOptionalTimestamp(receipt.readAt) || !isOptionalTimestamp(receipt.dismissedAt)
    || receipt.seenAt === null || (action === 'read' && receipt.readAt === null) || (action === 'dismiss' && receipt.dismissedAt === null)) return null;
  for (const field of ['seenAt', 'readAt', 'dismissedAt'] as const) {
    if (notice[field] !== null && receipt[field] !== notice[field]) return null;
  }
  return { ...notice, seenAt: receipt.seenAt, readAt: receipt.readAt, dismissedAt: receipt.dismissedAt, version: receipt.version };
}
